import { browser } from "$app/env";
import { toast } from "svelte-sonner";
import { ApiError, API_BASE_URL } from "#lib/api-client.js";
import { getRealtimeToken } from "#lib/apis/notifications-api.js";
import { auth } from "#lib/auth.svelte.js";
import { notifications } from "#lib/realtime/notifications.svelte.js";
import { presence } from "#lib/stores/presence.svelte.js";

const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

export type RealtimeStatus = "idle" | "connecting" | "open" | "closed";

interface ServerFrame {
	type?: string;
	content?: string;
	user_id?: string;
	user_name?: string;
	notification?: import("#lib/api-client/types.gen.js").Notification;
	unread_count?: number;
	/** Presence payload for presence.changed / presence.snapshot frames. */
	online?: boolean | string[];
	status?: string;
}

/**
 * Builds the WebSocket URL from the API base URL. The handshake lives at the
 * server root (`/ws`), not under `/api/v1`, and upgrades to `wss` whenever the
 * API is served over TLS so production traffic is encrypted end to end.
 */
export function realtimeUrl(apiBaseUrl: string): string {
	const root = apiBaseUrl.replace(/\/api\/v1\/?$/, "");
	return `${root.replace(/^http/, "ws")}/ws`;
}

/**
 * The single WebSocket connection for this browser session.
 *
 * One connection is opened after hydration or login, authenticated with a
 * short-lived token fetched from the API (the session cookie is HttpOnly and
 * cannot be read from JavaScript). It carries live notifications and presence
 * updates, and reconnects with exponential backoff when the connection drops.
 */
class RealtimeClient {
	status = $state<RealtimeStatus>("idle");
	/**
	 * Increments every time an established session reconnects (not the first
	 * connect). Views with socket-derived snapshots watch it to re-sync: the
	 * notifications bell re-reads the list, the users page re-reads presence.
	 */
	reconnects = $state(0);

	#socket: WebSocket | null = null;
	/** True once a socket has reached the authenticated state in this session. */
	#everConnected = false;
	/** Window listeners that report mouse/keyboard activity while connected. */
	#activityListeners: Array<{ type: string; handler: EventListener; target: EventTarget }> = [];
	#lastActivitySent = 0;
	#reconnectTimer: ReturnType<typeof setTimeout> | null = null;
	#attempts = 0;
	#connecting = false;
	#stopped = false;

	/** Opens the connection if one is not already open or opening. */
	async connect(): Promise<void> {
		if (!browser || this.#socket || this.#connecting) return;
		this.#connecting = true;
		this.#stopped = false;
		this.status = "connecting";
		try {
			const { token } = await getRealtimeToken();
			if (this.#stopped) return;
			const socket = new WebSocket(realtimeUrl(API_BASE_URL));
			this.#socket = socket;
			socket.onopen = () => {
				socket.send(JSON.stringify({ type: "auth", token }));
			};
			socket.onmessage = (event: MessageEvent<string>) => this.#handleFrame(event.data);
			socket.onclose = () => {
				this.#socket = null;
				this.status = "closed";
				this.#scheduleReconnect();
			};
			socket.onerror = () => {
				// A close event follows; the reconnect logic lives there.
				console.warn("Realtime socket error");
			};
		} catch (error) {
			if (error instanceof ApiError && error.status === 401) {
				// The session is gone (logout, expiry, cookie cleared). Retrying
				// would hammer /auth/ws-token every few seconds while the user sits
				// on the login page for nothing, so stop instead. A later login
				// calls connect() again, which clears #stopped.
				this.#stopped = true;
				this.status = "idle";
				return;
			}
			console.warn("Realtime connection failed:", error);
			this.#scheduleReconnect();
		} finally {
			this.#connecting = false;
		}
	}

	/** Closes the connection and stops reconnecting (logout / teardown). */
	disconnect(): void {
		this.#stopped = true;
		this.#removeActivityTracking();
		if (this.#reconnectTimer) {
			clearTimeout(this.#reconnectTimer);
			this.#reconnectTimer = null;
		}
		this.#attempts = 0;
		this.#everConnected = false;
		this.reconnects = 0;
		if (this.#socket) {
			this.#socket.onclose = null;
			this.#socket.close();
			this.#socket = null;
		}
		this.status = "idle";
	}

	/**
	 * Reports mouse/keyboard activity over the socket so the backend can tell
	 * online from away. Frames are throttled: the time clock only needs a
	 * heartbeat every few seconds, not every mouse move.
	 */
	#installActivityTracking(): void {
		if (!browser || this.#activityListeners.length > 0) return;
		const report = () => {
			const socket = this.#socket;
			if (!socket || socket.readyState !== WebSocket.OPEN) return;
			const now = Date.now();
			if (now - this.#lastActivitySent < 5_000) return;
			this.#lastActivitySent = now;
			try {
				socket.send(JSON.stringify({ type: "activity" }));
			} catch {
				// The socket is closing; the reconnect path handles it.
			}
		};
		const onActivity = () => report();
		const listeners: Array<{
			type: string;
			target: EventTarget;
			options?: AddEventListenerOptions;
		}> = [
			{ type: "mousemove", target: window, options: { passive: true } },
			{ type: "keydown", target: window, options: { passive: true } },
			{ type: "scroll", target: window, options: { passive: true } },
			{ type: "touchstart", target: window, options: { passive: true } },
			{ type: "focus", target: window },
		];
		for (const listener of listeners) {
			listener.target.addEventListener(listener.type, onActivity, listener.options);
			this.#activityListeners.push({
				type: listener.type,
				handler: onActivity,
				target: listener.target,
			});
		}
		report();
	}

	#removeActivityTracking(): void {
		for (const listener of this.#activityListeners) {
			listener.target.removeEventListener(listener.type, listener.handler);
		}
		this.#activityListeners = [];
	}

	#scheduleReconnect(): void {
		if (this.#stopped || this.#reconnectTimer) return;
		const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** this.#attempts);
		this.#attempts += 1;
		this.#reconnectTimer = setTimeout(() => {
			this.#reconnectTimer = null;
			void this.connect();
		}, delay);
	}

	#handleFrame(raw: string): void {
		let frame: ServerFrame;
		try {
			frame = JSON.parse(raw) as ServerFrame;
		} catch {
			console.warn("Realtime socket sent a malformed frame");
			return;
		}
		switch (frame.type) {
			case "connected":
				this.status = "open";
				this.#attempts = 0;
				this.#installActivityTracking();
				if (this.#everConnected) {
					// The socket dropped and came back: frames sent while it was gone
					// were never delivered, so reconcile the bell with the API once.
					this.reconnects += 1;
					void notifications.resync();
				}
				this.#everConnected = true;
				return;
			case "error":
				console.warn("Realtime socket error frame:", frame.content ?? "unknown");
				return;
			case "notification.created":
				if (frame.notification) {
					notifications.receive(frame.notification, frame.unread_count);
				}
				return;
			case "presence.changed":
				if (frame.user_id) {
					const status =
						frame.status ??
						(frame.online === false ? "offline" : frame.online === true ? "online" : "offline");
					presence.setStatus(frame.user_id, status);
				}
				return;
			case "presence.snapshot":
				if (Array.isArray(frame.online)) {
					presence.replace(
						(frame.online as Array<string | { id: string; status: string }>).map((entry) =>
							typeof entry === "string"
								? { id: entry, status: "online" as const }
								: { id: entry.id, status: entry.status as "online" | "away" | "offline" },
						),
					);
				}
				return;
			case "session.expired":
				// The server revoked the session (idle timeout) and closed the
				// socket; clear local auth and send the user back to login.
				toast.error(frame.content ?? "Your session ended. Please sign in again.");
				this.disconnect();
				void auth.logout();
				return;
			default:
				// Forward-compatible: ignore unknown frame types.
				return;
		}
	}
}

export const realtime = new RealtimeClient();

/**
 * Starts the session's single realtime connection and hydrates notifications.
 * Called from the authenticated layout; safe to call repeatedly.
 */
export function startRealtime(): void {
	// Never open a socket (or fetch a handshake token) without a signed-in user:
	// the login and 2FA pages are public, and an unauthenticated connect would
	// just 401 and retry.
	if (!browser || !auth.user?.id) return;
	void notifications.hydrate();
	void realtime.connect();
}

/** Stops the realtime connection and clears per-session state (logout). */
export function stopRealtime(): void {
	if (!browser) return;
	realtime.disconnect();
	notifications.reset();
	presence.clear();
}
